import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_PERIODS,
  RECEIVING_PERIODS,
  RECEIVE_PERIOD_METADATA,
  RECEIVE_PERIOD_FAILURE,
  SAVING_PERIOD,
  SAVING_PERIOD_FAILURE,
  UPDATING_PERIOD,
  UPDATING_PERIOD_FAILURE,
  DELETE_PERIOD,
  DELETING_PERIOD,
  DELETING_PERIOD_FAILURE,
  CLEAR_PERIOD_STATE,
  RESET_PERIOD_META,
  CLEAR_STATE,
  RECEIVE_PERIODS_QUOTE,
  RECEIVING_PERIODS_QUOTE,
  RECEIVE_PERIODS_QUOTE_FAILURE,
  RECEIVE_JES,
  RECEIVE_APS,
  RECEIVE_AR,
  RECEIVE_POS,
  RECEIVE_PCS,
  RECEIVE_CHECKS,
  RECEIVE_PP,
  RECEIVE_BP,
  RECEIVE_PAYMENTS
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const periodSchema = new schema.Entity('periods')
const periodListSchema = new schema.Array(periodSchema)
const jeSchema = new schema.Entity('journalentry')
const apSchema = new schema.Entity('accountspayable')
const arSchema = new schema.Entity('accountsreceivable')
const pcSchema = new schema.Entity('pettycash')
const poSchema = new schema.Entity('purchaseorder')
const ppSchema = new schema.Entity('prepayment')
const bpSchema = new schema.Entity('billpayment')
const checkSchema = new schema.Entity('papercheck')
const paymentSchema = new schema.Entity('payment')
const jeListSchema = new schema.Array(jeSchema)
const apListSchema = new schema.Array(apSchema)
const arListSchema = new schema.Array(arSchema)
const pcListSchema = new schema.Array(pcSchema)
const poListSchema = new schema.Array(poSchema)
const ppListSchema = new schema.Array(ppSchema)
const bpListSchema = new schema.Array(bpSchema)
const checkListSchema = new schema.Array(checkSchema)
const paymentListSchema = new schema.Array(paymentSchema)
const listSchemas = {
  journalentry: { schema: jeListSchema, mutations: { receive: RECEIVE_JES } },
  accountspayable: { schema: apListSchema, mutations: { receive: RECEIVE_APS } },
  accountsreceivable: { schema: arListSchema, mutations: { receive: RECEIVE_AR } },
  pettycash: { schema: pcListSchema, mutations: { receive: RECEIVE_PCS } },
  purchaseorder: { schema: poListSchema, mutations: { receive: RECEIVE_POS } },
  papercheck: { schema: checkListSchema, mutations: { receive: RECEIVE_CHECKS } },
  prepayment: { schema: ppListSchema, mutations: { receive: RECEIVE_PP } },
  billpayment: { schema: bpListSchema, mutations: { receive: RECEIVE_BP } },
  payment: { schema: paymentListSchema, mutations: { receive: RECEIVE_PAYMENTS } }
}

const state = {
  data: {},
  quote: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  savingFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: null,
  receivingQuote: false,
  receiveQuoteFailure: null
}

const actions = {
  fetchPeriodsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PERIODS, true)
    return http.get(buildUrlWithQuery('periods', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, periodListSchema)
          this.commit(RECEIVING_PERIODS, false)
          this.commit(RECEIVE_PERIODS, !result || !result.length ? results : entities.periods)
          this.commit(RECEIVE_PERIOD_METADATA, { next, previous, count, result: result.length })
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVING_PERIODS, false)
        this.commit(RECEIVE_PERIOD_FAILURE, handleXHRError(e))
      })
  },
  fetchPeriodsQuote (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PERIODS_QUOTE, true)
    return http.get(buildUrlWithQuery('periods/quote', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_PERIODS_QUOTE, false)
        if (!r || !Object.keys(r).length) return Promise.reject('No response')
        let quote = { closes: {}, moves: {} }
        if (r.closes && Object.keys(r.closes).length) {
          for (let c in r.closes) {
            if (listSchemas[c]) {
              let { entities, result } = normalize(r.closes[c], listSchemas[c].schema)
              this.commit(listSchemas[c].mutations.receive, { data: entities[c] })
              quote.closes[c] = result
            }
          }
        }
        if (r.moves && Object.keys(r.moves).length) {
          for (let m in r.moves) {
            if (listSchemas[m]) {
              let { entities, result } = normalize(r.moves[m], listSchemas[m].schema)
              this.commit(listSchemas[m].mutations.receive, { data: entities[m] })
              quote.moves[m] = result
            }
          }
        }
        this.commit(RECEIVE_PERIODS_QUOTE, quote)
        return quote
      })
      .catch(e => {
        console.log(e)
        this.commit(RECEIVING_PERIODS_QUOTE, false)
        return this.commit(RECEIVE_PERIODS_QUOTE_FAILURE, e || 'Error fetching periods quote')
      })
  },
  fetchCurrentAndNextPeriod (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return http.get(buildUrlWithQuery('periods', { active: ['True'] }, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        if (r.hasOwnProperty('results')) {
          const { results } = r
          if (results[0]) {
            const nextId = results[0].id + 1
            const nextUrl = buildUrl('periods', [{ id: [nextId] }, 'in'], instanceUrl)
            return http.get(nextUrl, { headers: getHeaders(this.getters.getToken) })
              .then(checkStatus)
              .then(parseJSON)
              .then((nextResponse) => {
                const nextResults = nextResponse.results
                let combinedResults
                if (nextResults && nextResults.length) {
                  combinedResults = [results[0], nextResults[0]]
                } else {
                  combinedResults = results
                }
                const { entities, result } = normalize(combinedResults, periodListSchema)
                if (!result) return nextResults
                this.commit(RECEIVE_PERIODS, entities.periods)
                this.commit(RECEIVE_PERIOD_METADATA)
                return r
              })
          }
        }
      })
      .catch((e) => {
        return this.commit(RECEIVE_PERIOD_FAILURE, handleXHRError(e))
      })
  },
  fetchPeriodsByPage (store, page) {
    const { previous, next } = store.state
    const pageUrl = previous || next
    if (pageUrl) {
      let url = new URL(pageUrl)
      url.searchParams.set('page', `${page}`)
      this.commit(RECEIVING_PERIODS, true)
      return http.get(url.href, { headers: getHeaders(this.getters.getToken) })
        .then(checkStatus)
        .then(parseJSON)
        .then(r => {
          this.commit(RECEIVING_PERIODS, false)
          if (!r) return Promise.reject('No response')
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, periodListSchema)
          this.commit(RECEIVE_PERIODS, !result ? r : entities.periods)
          this.commit(RECEIVE_PERIOD_METADATA, { next, previous, count, result: result.length })
          return entities
        })
        .catch(e => {
          this.commit(RECEIVING_PERIODS, false)
          return this.commit(RECEIVE_PERIOD_FAILURE, handleXHRError(e))
        })
    }
  },
  savePeriod (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(SAVING_PERIOD, true)
    if (payload) {
      return http.post(buildUrl('periods', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
        .then(r => {
          this.commit(SAVING_PERIOD, false)
          if (!r) return Promise.reject('No response')
          const { entities, result } = normalize(r, periodSchema)
          this.commit(RECEIVE_PERIODS, !result ? r : entities.periods)
          return r
        })
        .catch(e => {
          this.commit(SAVING_PERIOD, false)
          this.commit(SAVING_PERIOD_FAILURE, e ? handleXHRError(e) : 'Error saving period')
        })
    } else {
      return this.commit(SAVING_PERIOD, false)
    }
  },
  updatePeriod (store, payload) {
    if (!payload) return this.commit(UPDATING_PERIOD_FAILURE, 'Error updating period')
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_PERIOD, { id, status: true })
    return http.patch(buildUrlWithQuery(`periods/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_PERIOD, { id, status: false })
        if (r && r.status === 200) {
          const { entities, result } = normalize(r.body, periodSchema)
          if (!result || (Array.isArray(result) && !result.length)) {
            return Promise.reject('Error normalizing response')
          }
          this.commit(RECEIVE_PERIODS, entities.periods)
          return r.body
        } else {
          return Promise.reject(r ? r.statusText : 'Failed to update period')
        }
      })
      .catch(e => {
        this.commit(UPDATING_PERIOD, { id, status: false })
        this.commit(RECEIVE_PERIOD_FAILURE, e || 'Error updating period')
        return this.commit(UPDATING_PERIOD_FAILURE, { id, err: e ? handleXHRError(e) : 'Error updating period' })
      })
  },
  fetchPeriodOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    this.commit(RECEIVING_PERIODS, true)
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PERIODS, true)
    return http({ url: buildUrl('periods', query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_PERIODS, false)
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities, result } = normalize(choices, periodListSchema)
          if (!result) return this.commit(RECEIVE_PERIOD_FAILURE, 'Error normalizing response')
          /*
          this.commit(RECEIVE_PERIODS, { next: '', r: entities.periods })
          */
          this.commit(RECEIVE_PERIODS, entities.periods)
          this.commit(RECEIVING_PERIODS, false)
          return r
        }
      })
      .catch(e => {
        this.commit(RECEIVING_PERIODS, false)
        this.commit(RECEIVE_PERIOD_FAILURE, handleXHRError(e))
      })
  }
}

const getters = {
  getPeriods: (state) => {
    if (state.data) {
      return Object.values(state.data)
        .map(v => Object.assign({}, v))
        .sort((a, b) => new Date(b.start) - new Date(a.start))
    }
    return []
  },
  getPeriodMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getCurrentPeriod: (state) => {
    const { data } = state
    for (let d in data) {
      if (data[d].active) {
        return data[d]
      }
    }
    return {}
  },
  getPeriodById: (state, rootState) => (id) => {
    const { data } = state
    for (let d in data) {
      if (data && data[d].id === id) {
        return data[d]
      }
    }
    return {}
  },
  getPeriodFetchStatus: (state) => {
    return state.receivingData
  },
  getPeriodSaveStatus: (state) => {
    return state.saving
  },
  getPeriodSaveFailure: (state) => {
    return state.savingFailure
  },
  getPeriodsQuote: (state) => {
    return state.quote || {}
  },
  getPeriodsQuoteFetchStatus: (state) => {
    return state.receivingQuote || false
  },
  getPeriodsQuoteFetchFailure: (state) => {
    return state.receiveQuoteFailure || false
  }
}

const mutations = {
  [RECEIVE_PERIODS] (state, results) {
    if (!results || !Object.keys(results).length) return state.data
    if (state.data && Object.keys(state.data).length) {
      let tmpState = Object.assign({}, state.data)
      for (let key in tmpState) {
        if (tmpState[key].hasOwnProperty('active')) {
          tmpState[key].active = false
        }
      }
      state.data = Object.assign({}, tmpState, results)
    } else {
      state.data = Object.assign({}, results)
    }
  },
  [RECEIVING_PERIODS] (state, payload) {
    state.receivingData = payload || false
  },
  [RECEIVE_PERIOD_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_PERIOD] (state, payload) {
    state.saving = payload || false
  },
  [SAVING_PERIOD_FAILURE] (state, payload) {
    state.savingFailure = payload
  },
  [UPDATING_PERIOD] (state, payload) {
    const { id, status } = payload
    state.updating = { id, status, err: null }
  },
  [UPDATING_PERIOD_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, status: false, err }
  },
  [DELETE_PERIOD] (state, payload) {
    let tempData = Object.assign({}, state.data)
    for (let t in tempData) {
      if (!tempData.hasOwnProperty(t)) continue
      if (tempData[t].id === payload) {
        delete tempData[t]
        state.count = state.count - 1
      }
    }
    state.data = Object.assign({}, tempData)
  },
  [DELETING_PERIOD] (state, payload) {
    state.deleting = payload || false
  },
  [DELETING_PERIOD_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { status: false, id, err }
  },
  [RECEIVE_PERIODS_QUOTE] (state, payload) {
    let movesContainer = []
    let closesContainer = []
    let { moves, closes } = payload
    if (moves && Object.keys(moves).length) {
      for (let m in moves) {
        const mTmp = moves[m].map(v => {
          return { id: v, type: m }
        })
        movesContainer = [ ...movesContainer, ...mTmp ]
      }
    }
    if (closes && Object.keys(closes).length) {
      for (let c in closes) {
        const cTmp = closes[c].map(v => {
          return { id: v, type: c }
        })
        closesContainer = [ ...closesContainer, ...cTmp ]
      }
    }
    state.quote = { moves: movesContainer, closes: closesContainer }
  },
  [RECEIVING_PERIODS_QUOTE] (state, payload) {
    state.receivingQuote = payload || false
  },
  [RECEIVE_PERIODS_QUOTE_FAILURE] (state, payload) {
    state.receiveQuoteFailure = payload
  },
  [RECEIVE_PERIOD_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_PERIOD_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_PERIOD_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
